import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';

import FormFieldsCompact from '../../components/form/fieldsCompact';
import Input from '../../components/form/input';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

@observer
class EditSaleColor extends Component {
    constructor(props) {
        super(props);
        this.state = {
            parentSaleSearch: '',
            parentSaleResults: [],
            searching: false,
        };
    }

    // isWeborder is the server-side gate (lib/sale-buyer-access.js) that lets a buyer
    // read their own order cross-customer. The backend now pins it (post-sale.js /
    // patch-sale.js: only genuine self-checkout or an admin/customer-admin may set it)
    // — this is UX only, hiding the toggle from staff who can no longer change it
    // anyway. Computed per-render (not in the constructor) so it reacts to isAdmin/
    // isCustomerAdmin, and built as a fresh array each time rather than mutating any
    // shared list — fields.get() itself already returns copies, never the live FIELDS.
    getSections = (isAdmin, isCustomerAdmin) => {
        const onlyFields = ['saleDate', 'dueDate', 'color', 'title', 'paymentMethod', 'status', 'kid', 'shippingMethod', 'shippingCode', 'trackingNumber', 'images', 'files'];
        if (isAdmin || isCustomerAdmin) {
            onlyFields.push('isWeborder');
        }
        //    static get(type, section, skipFields = [], onlyFields = []) {
        return fields.get('sale', ['Sale basics', 'Sale shipping', 'Sale media', 'Sale price and payment'], [], onlyFields);
    }

    onSearchParentSale = (e) => {
        const search = e.target.value;
        this.setState({ parentSaleSearch: search });

        clearTimeout(this.searchTimer);
        if (search.length >= 2) {
            this.searchTimer = setTimeout(() => this.doParentSaleSearch(search), 300);
        } else {
            this.setState({ parentSaleResults: [] });
        }
    }

    doParentSaleSearch = async (search) => {
        this.setState({ searching: true });
        const { saleStore } = this.props.stores;

        // If search is a number, search by saleNumber directly
        const isNumeric = /^\d+$/.test(search.trim());
        const query = isNumeric
            ? { saleNumber: parseInt(search.trim(), 10), limit: 20 }
            : { search, limit: 20 };

        const results = await saleStore.load({
            query,
            skipUpdate: true,
        });
        this.setState({
            parentSaleResults: results || [],
            searching: false,
        });
    }

    selectParentSale = async (e) => {
        const { id, salenumber } = e.target.closest('button').dataset;
        const { saleStore } = this.props.stores;
        const { newSale } = saleStore;

        await saleStore.save({
            id: newSale.id,
            parentSaleId: parseInt(id, 10),
            parentSaleNumber: parseInt(salenumber, 10),
        });

        saleStore.updateObjectKeyValue('newSale', 'parentSaleId', parseInt(id, 10));
        saleStore.updateObjectKeyValue('newSale', 'parentSaleNumber', parseInt(salenumber, 10));

        this.setState({
            parentSaleSearch: '',
            parentSaleResults: [],
        });
    }

    selectCustomer = async (e) => {
        const dialogAction = beginDialogAction(this, 'selectCustomer', () => [
            this.props?.stores?.saleStore?.newSale?.id,
        ]);
        if (!dialogAction) return false;
        try {
            const { id } = e.target.closest('button').dataset;
            const { saleStore, userStore } = this.props.stores;
            const { newSale } = saleStore;
            const { currentCustomers = [] } = userStore;

            const customerId = parseInt(id, 10);

            // Don't do anything if already selected
            if (customerId === newSale?.customer) {
                return;
            }

            const newCustomer = currentCustomers.find(c => c.id === customerId);
            const confirmMessage = `Are you sure you want to change the customer for this sale to "${newCustomer?.name}"?`;

            if (!((await appConfirm(confirmMessage)) && dialogAction.isCurrent())) {
                return;
            }

            await saleStore.save({
                id: newSale.id,
                customer: customerId,
            });

            saleStore.updateObjectKeyValue('newSale', 'customer', customerId);
        } finally {
            dialogAction.finish();
        }
    }

    render() {
        const { week, isNew, drawerLevel, installation, lookupArray = 'sales' } = this.props;
        const { sectionsOther, showSimilar, parentSaleSearch, parentSaleResults, searching } = this.state;
        const { userStore, saleStore } = this.props.stores;
        const { user, isAdmin, isCustomerAdmin, currentCustomers = [] } = userStore;
        const { newSale } = saleStore;
        const sections = this.getSections(isAdmin, isCustomerAdmin);
        // const { newNews, foundList, foundListKeys } = saleStore;
        // const darkmode= util.getNestedValue(user, 'settings.darkmode');

        // Check if this is a credit note without parent sale link
        const isCreditNote = newSale?.paymentMethod === 'creditNote' || newSale?.status === 6;
        const missingParentSale = !newSale?.parentSaleId;
        const showParentSaleSearch = isCreditNote && missingParentSale;

        return (<>
            <div class='w-100 d-flex justify-content-center position-relative'>
                <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>
                    {/* <div class='w-100 d-flex flex-column justify-content-start'>
                        <xmp>{JSON.stringify(user.settings, null, 2)}</xmp>
                    </div> */}
                    {/* <xmp>{JSON.stringify(newCustomer, null, 4)}</xmp> */}
                    {/* <xmp>{JSON.stringify(installation, null, 4)}</xmp> */}
                    <div class='font-weight-lighter px-3 box-header mt-0'>
                        <Text id='sale.edit'>Edit sale</Text>
                    </div>

                    {/* Warning for saleDate changes on processed sales */}
                    {newSale?.status > 0 && newSale?.status !== 2 && (isCustomerAdmin || isAdmin) && (
                        <div class='w-100 d-flex flex-column justify-content-start mb-3'>
                            <div class='bg-warning-subtle border border-warning rounded p-3 mx-3'>
                                <div class='fw-bold mb-1'>
                                    <i class='fa-solid fa-warning text-warning me-2' />
                                    <Text id='sale.warning-date-change'>Advarsel: Endring av salgsdato</Text>
                                </div>
                                <div class='small text-muted'>
                                    <Text id='sale.warning-date-change-desc'>Dette salget er allerede behandlet. Endring av salgsdato kan påvirke regnskapsrapporter.</Text>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Parent sale search for credit notes missing relation */}
                    {showParentSaleSearch && (
                        <div class='w-100 d-flex flex-column justify-content-start mb-3'>
                            <div class='bg-warning-subtle border border-warning rounded p-3 mx-3'>
                                <div class='fw-bold mb-2'>
                                    <i class='fa-solid fa-warning text-warning me-2' />
                                    <Text id='sale.credit-note-missing-parent'>Kreditnota mangler kobling til original faktura</Text>
                                </div>
                                <div class='mb-2'>
                                    <Text id='sale.search-parent-sale'>Søk etter original faktura:</Text>
                                </div>
                                <div class='position-relative'>
                                    <input
                                        type='text'
                                        class='form-control'
                                        placeholder='Søk på salgnummer, kundenavn...'
                                        value={parentSaleSearch}
                                        onInput={this.onSearchParentSale}
                                    />
                                    {searching && (
                                        <span class='position-absolute' style='right: 10px; top: 8px;'>
                                            <i class='fa-duotone fa-spinner fa-spin' />
                                        </span>
                                    )}
                                </div>
                                {parentSaleResults.length > 0 && (
                                    <div class='mt-2 border rounded bg-white' style='max-height: 300px; overflow-y: auto;'>
                                        {parentSaleResults.map(sale => {
                                            const visitor = sale.visitors?.[0];
                                            const visitorName = visitor ? `${visitor.firstname || ''} ${visitor.lastname || ''}`.trim() : '';
                                            const { language = 'no' } = user;

                                            // Determine status label (same logic as listSaleMini.js)
                                            let statusText = '';
                                            let statusClass = '';
                                            if (sale.paymentMethod === 'creditNote' || sale.paymentMethod === 'invoiceCredited' || sale.paymentMethod === 'saleCredited') {
                                                statusText = saleStore.getPaymentMethodText(sale.paymentMethod, language);
                                                statusClass = 'secondary';
                                            } else if (sale.status === 1 || sale.paidDate) {
                                                statusText = saleStore.getSaleStatusById(1, language);
                                                statusClass = 'success';
                                            } else {
                                                statusText = saleStore.getSaleStatusById(sale.status, language);
                                                statusClass = saleStore.getSaleStatusClass(saleStore.getSaleStatusById(sale.status)) || 'secondary';
                                            }

                                            return (
                                                <button
                                                    type='button'
                                                    class='btn btn-light w-100 text-start border-bottom rounded-0 py-2'
                                                    data-id={sale.id}
                                                    data-salenumber={sale.saleNumber}
                                                    onClick={this.selectParentSale}
                                                >
                                                    <div class='d-flex justify-content-between align-items-center'>
                                                        <div>
                                                            <span class='fw-bold'>#{sale.saleNumber}</span>
                                                            <span class='text-muted ms-2'>{util.formatDate(sale.saleDate, { locale: 'nb-NO' })}</span>
                                                            {visitorName && <span class='ms-2'>{visitorName}</span>}
                                                            <span class={`badge bg-${statusClass} ms-2`}>{statusText}</span>
                                                        </div>
                                                        <div class='text-end'>
                                                            <span class='fw-bold'>{saleStore.sumTotal(sale.products)}</span>
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Customer selection section - only show if more than one customer */}
                    {(isCustomerAdmin || isAdmin) && currentCustomers.length > 1 && (
                        <div class='w-100 d-flex flex-column justify-content-start'>
                            <div class='bg-light d-flex flex-column justify-content-start h-100 pt-3 pb-3'>
                                <div class='fw-bold mb-2 px-3'>
                                    <i class='fa-solid fa-building me-2' />
                                    <Text id='sale.customer'>Customer</Text>
                                </div>
                                <div class='d-flex flex-wrap gap-2 px-3'>
                                    {currentCustomers.map(customer => {
                                        const isSelected = customer.id === newSale?.customer;
                                        return (
                                            <button
                                                type='button'
                                                class={`btn btn-sm rounded-pill ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                data-id={customer.id}
                                                onClick={this.selectCustomer}
                                            >
                                                {customer.name}
                                                {customer.orgNumber && <span class='ms-1 opacity-75'>({customer.orgNumber})</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    <div class='w-100 d-flex flex-column justify-content-start'>
                        <div class='bg-light d-flex flex-column justify-content-start h-100 pt-3'>
                            <FormFieldsCompact
                                stores={this.props.stores}
                                sections={sections}
                                holdingStore={saleStore}
                                objectName={'newSale'}
                                drawerLevel={drawerLevel}
                                isAdmin={false}
                                lookupArray={lookupArray}
                                // rightHeader={isNew && foundList?.length > 0 ? <span class='text-success font-weight-bold'><i class='fa-solid fa-circle-info' /> Similar newss ({foundList.length})</span> : null}
                                // rightHeaderClick={this.toggleSimilar}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>);
    }
}

export default EditSaleColor;
